import React, { useEffect, useState } from 'react'
import { fetchCmsItems, getOptimizedImageUrl } from '../lib/cms'
import ImageLightbox from '../components/ImageLightbox'

export default function GalleryPage() {
  const [galleries, setGalleries] = useState([])
  const [activeCategory, setActiveCategory] = useState('alle')
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [allImages, setAllImages] = useState([])
  const [lightboxIndex, setLightboxIndex] = useState(0)

  useEffect(() => {
    document.title = 'Galleri - SKI & CO TRAVEL ApS'
    async function loadGalleries() {
      const data = await fetchCmsItems('galleri')
      setGalleries(data)

      // Collect flat list of images
      const imagesList = []
      data.forEach((gal) => {
        if (Array.isArray(gal.billeder)) {
          gal.billeder.forEach((imgUrl, i) => {
            const captions = gal.billedtekster ? gal.billedtekster.split(',') : []
            const alts = gal.alt_tekster ? gal.alt_tekster.split(',') : []
            imagesList.push({
              url: imgUrl,
              caption: captions[i]?.trim() || gal.gallerinavn,
              alt: alts[i]?.trim() || gal.gallerinavn,
              category: gal.kategori || 'Generelt',
            })
          })
        }
      })
      setAllImages(imagesList)
    }
    loadGalleries()
  }, [])

  const categories = ['alle', ...new Set(galleries.map((g) => g.kategori).filter(Boolean))]

  const filteredImages = activeCategory === 'alle'
    ? allImages
    : allImages.filter((img) => img.category === activeCategory)

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 space-y-10">
      <div className="space-y-3">
        <span className="text-xs font-bold uppercase tracking-widest text-cyan-400">
          SKI & CO TRAVEL ApS
        </span>
        <h1 className="font-heading font-extrabold text-2xl sm:text-4xl text-white">
          Billedgalleri
        </h1>
        <p className="text-slate-300 text-sm sm:text-base max-w-2xl leading-relaxed">
          Gå på opdagelse i stemningsfulde billeder fra vores fantastiske destinationer, håndplukkede hoteller og uforglemmelige alpeoplevelser.
        </p>
      </div>

      {/* Category Pills */}
      <div className="flex flex-wrap items-center gap-2 pt-2 border-b border-slate-800 pb-4">
        {categories.map((cat) => (
          <button
            key={cat}
            onClick={() => setActiveCategory(cat)}
            className={`px-4 py-2 rounded-xl text-xs font-semibold capitalize transition-colors ${
              activeCategory === cat
                ? 'bg-cyan-500 text-slate-950 shadow-md shadow-cyan-500/20'
                : 'bg-slate-900 text-slate-300 hover:bg-slate-800'
            }`}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Image Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
        {filteredImages.map((img, idx) => (
          <div
            key={idx}
            onClick={() => {
              setLightboxIndex(idx)
              setLightboxOpen(true)
            }}
            className="group relative h-64 rounded-2xl overflow-hidden border border-slate-800 bg-slate-900 shadow-xl cursor-pointer hover:border-cyan-400 transition-all duration-300"
          >
            <img
              src={getOptimizedImageUrl(img.url, 800, 80)}
              alt={img.alt}
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/20 to-transparent opacity-80 group-hover:opacity-60 transition-opacity" />

            <div className="absolute bottom-4 left-4 right-4 space-y-1">
              <span className="text-[10px] uppercase font-bold tracking-widest text-cyan-400">
                {img.category}
              </span>
              <p className="text-white font-semibold text-sm line-clamp-1">
                {img.caption}
              </p>
            </div>
          </div>
        ))}
      </div>

      <ImageLightbox
        images={filteredImages}
        currentIndex={lightboxIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        onPrev={() => setLightboxIndex((prev) => (prev > 0 ? prev - 1 : filteredImages.length - 1))}
        onNext={() => setLightboxIndex((prev) => (prev < filteredImages.length - 1 ? prev + 1 : 0))}
      />
    </div>
  )
}
